import { PaymentMethod } from "model/Gig";
import { TimeReportResolution, TimeReportStatusV2 } from "model/TimeReport";
import type { TimeReportV2Detail } from "services/api/company/timeReports.service";
import {
  canEditTimeReport,
  resolveTimeReportActionPolicy,
} from "web-apps/company/components/timeReportsV2/components/views/person/models/timeReportActionPolicy";
import {
  isReportedTimeWindowValid,
  minutesBetweenIso,
  normalizeTimeReportTimesOvernight,
  parseIsoDateToLocalDateOrNull,
  toTimeInputValue,
} from "web-apps/company/utils/timeAndDate";
import { resolveEditedTimeReportTimes } from "./timeReportDetailsPanel.editTimes.logic";

interface DeriveTimeReportDetailsParams {
  detail: TimeReportDetailsPanelModel | null;
  editedBreakEnd: string;
  editedBreakStart: string;
  editedReportedEnd: string;
  editedReportedStart: string;
  isEditMode: boolean;
}

interface TimeSources {
  reportedStart: string | null;
  reportedEnd: string | null;
  plannedStart: string | null;
  plannedEnd: string | null;
  stampedStart: string | null;
  stampedEnd: string | null;
  breakStart: string | null;
  breakEnd: string | null;
  plannedBreakStart: string | null;
  plannedBreakEnd: string | null;
}

interface DerivedTimes {
  breakMinutes: number;
  currentSignedOvertimeMinutes: number;
  currentTotalMinutes: number;
  hasReportedTimeChanges: boolean;
  isEditValid: boolean;
  plannedMinutes: number;
}

interface CurrentTimes {
  reportedStart: string | null;
  reportedEnd: string | null;
  breakStart: string | null;
  breakEnd: string | null;
}

interface DerivedActions {
  canEdit: boolean;
  hasVisibleActions: boolean;
  showRetractAction: boolean;
  showAbsenceAction: boolean;
  showRejectAction: boolean;
  showApproveAction: boolean;
  showResetAction: boolean;
}

interface DerivedComments {
  userComment: string;
  companyComment: string;
  noComments: boolean;
}

interface DerivedWorkerInfo {
  workerName: string;
  workerRole: string;
  workGroupName: string | null;
  reportUpdatedAt: string | null;
}

interface TimeReportDetailsPanelWorker {
  firstName: string;
  lastName: string;
  externalId: string | null;
  role: string;
}

interface TimeReportDetailsPanelGigShift {
  startTime: string | null;
  endTime: string | null;
  breakStartTime: string | null;
  breakEndTime: string | null;
}

export interface TimeReportDetailsPanelModel {
  id: number;
  status: TimeReportStatusV2;
  resolution: TimeReportResolution;
  paymentType: PaymentMethod;
  startTime: string | null;
  endTime: string | null;
  breakStartTime: string | null;
  breakEndTime: string | null;
  stampedStartTime: string | null;
  stampedEndTime: string | null;
  companyNotes: string;
  userNotes: string;
  updatedAt: string;
  worker: TimeReportDetailsPanelWorker;
  workGroupName: string | null;
  gigShift: TimeReportDetailsPanelGigShift;
}

export type TimeReportDetailsDerived = TimeSources &
  DerivedActions &
  DerivedComments &
  DerivedWorkerInfo & {
    breakMinutes: number;
    dateSource: string | null;
    effectiveSignedOvertimeMinutes: number;
    effectiveTotalMinutes: number;
    hasReportedTimeChanges: boolean;
    isEditValid: boolean;
    plannedMinutes: number;
    resolution: TimeReportResolution | null;
    status: TimeReportStatusV2 | null;
    timeReportId: number | null;
  };

/**
 * Maps raw time report detail API data to the panel model used by the details UI.
 */
export const mapTimeReportDetailToPanelModel = (
  detail: TimeReportV2Detail,
): TimeReportDetailsPanelModel => {
  return {
    id: detail.id,
    status: detail.status,
    resolution: detail.resolution,
    paymentType: detail.paymentType,
    startTime: detail.startTime,
    endTime: detail.endTime,
    breakStartTime: detail.breakStartTime,
    breakEndTime: detail.breakEndTime,
    stampedStartTime: detail.stampedStartTime,
    stampedEndTime: detail.stampedEndTime,
    companyNotes: detail.companyNotes ?? "",
    userNotes: detail.userNotes ?? "",
    updatedAt: detail.updatedAt,
    worker: {
      firstName: detail.worker.firstName,
      lastName: detail.worker.lastName,
      externalId: detail.worker.externalId,
      role: detail.worker.role ?? "",
    },
    workGroupName: detail.structure.workGroupName ?? null,
    gigShift: {
      startTime: detail.gigShift.startTime ?? null,
      endTime: detail.gigShift.endTime ?? null,
      breakStartTime: detail.gigShift.breakStartTime ?? null,
      breakEndTime: detail.gigShift.breakEndTime ?? null,
    },
  };
};

const resolveRawTimeSources = (
  detail: TimeReportDetailsPanelModel | null,
): TimeSources => ({
  reportedStart: detail?.startTime ?? null,
  reportedEnd: detail?.endTime ?? null,
  plannedStart: detail?.gigShift.startTime ?? null,
  plannedEnd: detail?.gigShift.endTime ?? null,
  stampedStart: detail?.stampedStartTime ?? null,
  stampedEnd: detail?.stampedEndTime ?? null,
  breakStart: detail?.breakStartTime ?? null,
  breakEnd: detail?.breakEndTime ?? null,
  plannedBreakStart: detail?.gigShift.breakStartTime ?? null,
  plannedBreakEnd: detail?.gigShift.breakEndTime ?? null,
});

interface CalendarDateCorrection {
  readonly normalized: string;
  readonly original: string | null;
}

function correctCalendarDate({
  normalized,
  original,
}: CalendarDateCorrection): string {
  const normalizedDate = parseIsoDateToLocalDateOrNull(normalized);
  const originalDate = parseIsoDateToLocalDateOrNull(original);
  const hasMatchingDate =
    normalizedDate != null &&
    originalDate != null &&
    normalizedDate.equals(originalDate);
  return hasMatchingDate ? (original ?? normalized) : normalized;
}

const resolveTimeSources = (
  detail: TimeReportDetailsPanelModel | null,
): TimeSources => {
  const sources = resolveRawTimeSources(detail);

  // Without a stamp-out, an unsubmitted report can contain an end date copied
  // from an overnight plan. Rebuild its clock values from the report start date
  // so display, edit, and approval all use the same corrected calendar dates.
  if (
    detail?.status !== TimeReportStatusV2.NEW ||
    detail.stampedEndTime != null
  ) {
    return sources;
  }

  const normalizedReportedTimes = resolveEditedTimeReportTimes({
    baseStartTime: sources.reportedStart,
    editedStart: toTimeInputValue(sources.reportedStart),
    editedEnd: toTimeInputValue(sources.reportedEnd),
    editedBreakStart: toTimeInputValue(sources.breakStart),
    editedBreakEnd: toTimeInputValue(sources.breakEnd),
  });

  if (!normalizedReportedTimes.ok) return sources;

  return {
    ...sources,
    reportedStart: correctCalendarDate({
      original: sources.reportedStart,
      normalized: normalizedReportedTimes.value.startTime,
    }),
    reportedEnd: correctCalendarDate({
      original: sources.reportedEnd,
      normalized: normalizedReportedTimes.value.endTime,
    }),
    breakStart: correctCalendarDate({
      original: sources.breakStart,
      normalized: normalizedReportedTimes.value.breakStartTime,
    }),
    breakEnd: correctCalendarDate({
      original: sources.breakEnd,
      normalized: normalizedReportedTimes.value.breakEndTime,
    }),
  };
};

const deriveTimes = (
  params: DeriveTimeReportDetailsParams,
  sources: TimeSources,
): DerivedTimes => {
  const breakMinutes = minutesBetweenIso(sources.breakStart, sources.breakEnd);
  const plannedMinutes = derivePlannedMinutes(sources);
  const currentTimes = resolveCurrentTimes(params, sources);
  const currentTotalMinutes = deriveCurrentTotalMinutes(currentTimes);
  const currentSignedOvertimeMinutes = currentTotalMinutes - plannedMinutes;
  const isEditValid = deriveIsEditValid(params.isEditMode, currentTimes);
  const hasReportedTimeChanges = deriveHasReportedTimeChanges(
    params.isEditMode,
    resolveRawTimeSources(params.detail),
    currentTimes,
  );

  return {
    breakMinutes,
    plannedMinutes,
    currentTotalMinutes,
    currentSignedOvertimeMinutes,
    isEditValid,
    hasReportedTimeChanges,
  };
};

const derivePlannedMinutes = (sources: TimeSources): number => {
  const plannedBreakMinutes = minutesBetweenIso(
    sources.plannedBreakStart,
    sources.plannedBreakEnd,
  );
  const plannedDurationMinutes = minutesBetweenIso(
    sources.plannedStart,
    sources.plannedEnd,
  );

  return Math.max(0, plannedDurationMinutes - plannedBreakMinutes);
};

const resolveCurrentTimes = (
  params: DeriveTimeReportDetailsParams,
  sources: TimeSources,
): CurrentTimes => {
  if (params.isEditMode) {
    const editedTimes = resolveEditedTimeReportTimes({
      baseStartTime: sources.reportedStart,
      editedBreakEnd: params.editedBreakEnd,
      editedBreakStart: params.editedBreakStart,
      editedEnd: params.editedReportedEnd,
      editedStart: params.editedReportedStart,
    });

    if (!editedTimes.ok) {
      return {
        reportedStart: null,
        reportedEnd: null,
        breakStart: null,
        breakEnd: null,
      };
    }

    return {
      reportedStart: editedTimes.value.startTime,
      reportedEnd: editedTimes.value.endTime,
      breakStart: editedTimes.value.breakStartTime,
      breakEnd: editedTimes.value.breakEndTime,
    };
  }

  const normalizedCurrentTimes = normalizeCurrentTimes({
    reportedStart: sources.reportedStart,
    reportedEnd: sources.reportedEnd,
    breakStart: sources.breakStart,
    breakEnd: sources.breakEnd,
  });

  if (normalizedCurrentTimes != null) {
    return normalizedCurrentTimes;
  }

  return {
    reportedStart: sources.reportedStart,
    reportedEnd: sources.reportedEnd,
    breakStart: sources.breakStart,
    breakEnd: sources.breakEnd,
  };
};

const normalizeCurrentTimes = (value: CurrentTimes): CurrentTimes | null => {
  if (
    value.reportedStart == null ||
    value.reportedEnd == null ||
    value.breakStart == null ||
    value.breakEnd == null
  ) {
    return null;
  }

  const normalizedTimes = normalizeTimeReportTimesOvernight({
    startTime: value.reportedStart,
    endTime: value.reportedEnd,
    breakStartTime: value.breakStart,
    breakEndTime: value.breakEnd,
  });

  if (normalizedTimes == null) {
    return null;
  }

  return {
    reportedStart: normalizedTimes.startTime,
    reportedEnd: normalizedTimes.endTime,
    breakStart: normalizedTimes.breakStartTime,
    breakEnd: normalizedTimes.breakEndTime,
  };
};

const deriveCurrentTotalMinutes = (currentTimes: CurrentTimes): number => {
  const currentBreakMinutes = minutesBetweenIso(
    currentTimes.breakStart,
    currentTimes.breakEnd,
  );
  const currentDurationMinutes = minutesBetweenIso(
    currentTimes.reportedStart,
    currentTimes.reportedEnd,
  );
  return Math.max(0, currentDurationMinutes - currentBreakMinutes);
};

const deriveIsEditValid = (
  isEditMode: boolean,
  currentTimes: CurrentTimes,
): boolean =>
  isEditMode &&
  isReportedTimeWindowValid({
    startTime: currentTimes.reportedStart,
    endTime: currentTimes.reportedEnd,
    breakStartTime: currentTimes.breakStart,
    breakEndTime: currentTimes.breakEnd,
  });

const deriveHasReportedTimeChanges = (
  isEditMode: boolean,
  sources: TimeSources,
  currentTimes: CurrentTimes,
): boolean => {
  if (!isEditMode) {
    return false;
  }

  const originalTimes = normalizeCurrentTimes({
    reportedStart: sources.reportedStart,
    reportedEnd: sources.reportedEnd,
    breakStart: sources.breakStart,
    breakEnd: sources.breakEnd,
  }) ?? {
    reportedStart: sources.reportedStart,
    reportedEnd: sources.reportedEnd,
    breakStart: sources.breakStart,
    breakEnd: sources.breakEnd,
  };

  return (
    originalTimes.reportedStart !== currentTimes.reportedStart ||
    originalTimes.reportedEnd !== currentTimes.reportedEnd ||
    originalTimes.breakStart !== currentTimes.breakStart ||
    originalTimes.breakEnd !== currentTimes.breakEnd
  );
};

const deriveActions = (
  detail: TimeReportDetailsPanelModel | null,
  status: TimeReportStatusV2 | null,
  resolution: TimeReportResolution | null,
): DerivedActions => {
  const policy = resolveTimeReportActionPolicy(status, resolution);

  const canEdit = detail != null && canEditTimeReport(status);

  return {
    canEdit,
    ...policy,
  };
};

/**
 * Derives display state and action flags for the time report details panel.
 * Includes edit-mode derived values, validation state, and action visibility.
 */
export const deriveTimeReportDetails = (
  params: DeriveTimeReportDetailsParams,
): TimeReportDetailsDerived => {
  const detail = params.detail;
  const sources = resolveTimeSources(detail);
  const derivedTimes = deriveTimes(params, sources);
  const derivedActions = deriveActions(
    detail,
    detail?.status ?? null,
    detail?.resolution ?? null,
  );
  const workerName = resolveWorkerName(detail);
  const userComment = resolveTrimmedComment(detail?.userNotes);
  const companyComment = resolveTrimmedComment(detail?.companyNotes);

  return {
    timeReportId: detail?.id ?? null,
    status: detail?.status ?? null,
    resolution: detail?.resolution ?? null,
    reportedStart: sources.reportedStart,
    reportedEnd: sources.reportedEnd,
    plannedStart: sources.plannedStart,
    plannedEnd: sources.plannedEnd,
    stampedStart: sources.stampedStart,
    stampedEnd: sources.stampedEnd,
    breakStart: sources.breakStart,
    breakEnd: sources.breakEnd,
    plannedBreakStart: sources.plannedBreakStart,
    plannedBreakEnd: sources.plannedBreakEnd,
    breakMinutes: derivedTimes.breakMinutes,
    plannedMinutes: derivedTimes.plannedMinutes,
    dateSource: sources.reportedStart,
    workerName,
    workerRole: detail?.worker.role ?? "",
    workGroupName: detail?.workGroupName ?? null,
    reportUpdatedAt: detail?.updatedAt ?? null,
    userComment,
    companyComment,
    noComments: userComment.length === 0 && companyComment.length === 0,
    canEdit: derivedActions.canEdit,
    hasVisibleActions: derivedActions.hasVisibleActions,
    showRetractAction: derivedActions.showRetractAction,
    showAbsenceAction: derivedActions.showAbsenceAction,
    showRejectAction: derivedActions.showRejectAction,
    showApproveAction: derivedActions.showApproveAction,
    showResetAction: derivedActions.showResetAction,
    effectiveTotalMinutes: derivedTimes.currentTotalMinutes,
    effectiveSignedOvertimeMinutes: derivedTimes.currentSignedOvertimeMinutes,
    isEditValid: derivedTimes.isEditValid,
    hasReportedTimeChanges: derivedTimes.hasReportedTimeChanges,
  };
};

const resolveWorkerName = (
  detail: TimeReportDetailsPanelModel | null,
): string => {
  if (detail == null) {
    return "";
  }

  return `${detail.worker.firstName} ${detail.worker.lastName}`;
};

const resolveTrimmedComment = (comment: string | null | undefined): string => {
  return comment?.trim() ?? "";
};
